// Content data for the Editorial portfolio — preserved verbatim from the prototype.

export interface Expertise {
  t: string;
  d: string;
  tech: string[];
}

export interface CareerEntry {
  date: string;
  role: string;
  org: string;
  tech?: string[];
  b: string[];
}

export interface WorkEntry {
  no: string;
  name: string;
  cat: string;
  d: string;
  tech: string[];
  link: string;
}

export const EXPERTISE: Expertise[] = [
  {
    t: "Full-Stack Web Dev",
    d: "I build diverse web applications from scratch with modern technologies like React and Flask, strong across the full SDLC on both frontend and backend.",
    tech: ["React", "TypeScript", "JavaScript", "HTML5", "CSS3", "SASS", "Flask", "Python", "SQL", "PostgreSQL"],
  },
  {
    t: "DevOps & Automation",
    d: "Once an application is built, I set up DevOps testing, CI/CD pipelines, and deployment automation to support a successful go-live.",
    tech: ["Git", "GitHub Actions", "Docker", "AWS", "Azure", "Linux", "Snowflake", "Selenium"],
  },
  {
    t: "GenAI & LLM",
    d: "Leveraging the latest AI models in production, with experience building enterprise-grade GenAI solutions for intelligent decision making.",
    tech: ["OpenAI", "Groq", "LangChain", "Qdrant", "Hugging Face", "LlamaIndex", "Streamlit"],
  },
];

export const CAREER: CareerEntry[] = [
  {
    date: "May 2026 → Present",
    role: "Software Engineer",
    org: "C3G · Montreal, QC (Co-op)",
    tech: ["Kubernetes", "Docker", "Helm", "Cloud Infrastructure", "Open Source"],
    b: [
      "Building Kubernetes deployment infrastructure for Bento, C3G's open-source genomics platform used by research institutions.",
      "Containerizing platform services with Docker and Helm for reproducible deployments, contributing Helm charts to the open-source repo.",
    ],
  },
  {
    date: "Mar 2026 → May 2026",
    role: "Full Stack Software Developer",
    org: "ABAKiS Solutions · Remote (Intern)",
    tech: ["Ruby on Rails", "ERB", "SCSS", "Devise", "PWA"],
    b: [
      "Built a mobile PWA layer for a multi-tenant ABA therapy SaaS with Rails, ERB, and SCSS, giving mobile access to 10+ client teams.",
      "Overrode Devise auth to route mobile users to a dedicated layout, resolving silent routing failures across 6 OS and browser patterns.",
    ],
  },
  {
    date: "Jan 2026 → Apr 2026",
    role: "Full Stack Software Developer",
    org: "Expense Trend · Remote (Intern)",
    tech: ["Next.js", "React", "TypeScript", "REST APIs", "Node.js"],
    b: [
      "Built responsive analytics dashboards in Next.js and React to visualize keyword difficulty, volume, and rank trends.",
      "Implemented client-side filtering and sorting, time-series charting, and API-driven state flows.",
    ],
  },
  {
    date: "Oct 2025 → Dec 2025",
    role: "AI Prompt Engineer",
    org: "FiscalElevate · Remote (Intern)",
    tech: ["ChatGPT API", "Claude API", "Airtable", "n8n", "QA Scoring"],
    b: [
      "Designed and tuned prompt workflows for AI cold-email generation using ChatGPT and Claude APIs.",
      "Ran batch testing loops in Airtable and n8n, scoring multi-email output sets for quality and compliance.",
    ],
  },
];

export const CAREER_EXTRA: CareerEntry[] = [
  {
    date: "May 2025 → Aug 2025",
    role: "Guest Services Representative",
    org: "Holiday Inn Express · Toronto, ON",
    tech: ["Process Design", "Google Sheets", "HotelKey PMS"],
    b: [
      "Built a shared shift-handoff system in Google Sheets for faster handovers and fewer misses.",
      "Worked in HotelKey PMS across high-volume check-in and check-out workflows.",
    ],
  },
  {
    date: "May 2021 → Aug 2024",
    role: "Guest Services Coordinator",
    org: "Holiday Inn · Sudbury, ON",
    tech: ["Workflow Design", "Microsoft Teams", "Onboarding"],
    b: [
      "Standardized onboarding and internal workflows for smoother front-desk operations.",
      "Coordinated cross-team communication using Microsoft Teams and shared tools.",
    ],
  },
];

export const WORK: WorkEntry[] = [
  {
    no: "01",
    name: "Diglott",
    cat: "Language · Clean Arch",
    d: "Word-level playback translator on DeepL and Google TTS, built on SOLID principles.",
    tech: ["Python", "DeepL API", "Google TTS", "Flask", "SOLID"],
    link: "https://github.com/nehan-p/diglott",
  },
  {
    no: "02",
    name: "Columns",
    cat: "Game · Low-Level",
    d: "A falling-block puzzle written from scratch in MIPS assembly with memory-mapped I/O.",
    tech: ["MIPS Assembly", "Memory I/O", "Bitwise Ops"],
    link: "https://github.com/nehan-p/columns",
  },
  {
    no: "03",
    name: "Twitter Viz",
    cat: "Data · Graphs",
    d: "Graph-based analysis of Twitter engagement and communities using NetworkX.",
    tech: ["Python", "NetworkX", "Pandas", "Matplotlib"],
    link: "https://github.com/nehan-p/twitter-visualizer",
  },
  {
    no: "04",
    name: "Health Insights",
    cat: "Data · Research",
    d: "CCHS analysis (8,000+ entries) on social connectedness vs. life satisfaction.",
    tech: ["Python", "Pandas", "Statsmodels", "Data Viz"],
    link: "https://docs.google.com/presentation/d/1D0HDcg542q_zsB0RekX41krOKtQDDRmD6zNX4BAkt34/edit",
  },
];

// Shared constants used across components.
export const RESUME_URL = `${process.env.PUBLIC_URL}/Nehan_Punjani_Resume.pdf`;
export const EMAIL = "nehan.punjani@mail.utoronto.ca";
export const GITHUB_URL = "https://github.com/nehan-p";
export const LINKEDIN_URL = "https://www.linkedin.com/in/nehan-punjani/";
